class OverviewWheelStory extends HTMLElement {
  constructor() {
    super();
    this.wrapper = null;
    this.items = [];
    this.totalSteps = 0;
    this.activeStep = 0;
    this.isWheeling = false;
    this.wheelTimeout = null;
    this.touchStartY = 0;

    this.handleScroll = this.handleScroll.bind(this);
    this.handleWheel = this.handleWheel.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
  }

  connectedCallback() {
    this.wrapper = this.closest('[id^="ows-wrapper-"]');
    this.items = Array.from(this.querySelectorAll("[data-ows-item]"));
    this.totalSteps = Number(this.dataset.totalSteps) || this.items.length * 2;

    if (!this.wrapper || this.items.length === 0) return;

    this.applyState(0);
    window.addEventListener("scroll", this.handleScroll, { passive: true });
    window.addEventListener("wheel", this.handleWheel, { passive: false });
    this.addEventListener("touchstart", this.handleTouchStart, { passive: true });
    this.addEventListener("touchmove", this.handleTouchMove, { passive: false });

    this.handleScroll();
  }

  disconnectedCallback() {
    window.removeEventListener("scroll", this.handleScroll);
    window.removeEventListener("wheel", this.handleWheel);
    this.removeEventListener("touchstart", this.handleTouchStart);
    this.removeEventListener("touchmove", this.handleTouchMove);
    if (this.wheelTimeout) {
      window.clearTimeout(this.wheelTimeout);
    }
  }

  isStuck() {
    if (!this.wrapper) return false;
    const top = this.wrapper.offsetTop;
    const maxScroll = this.wrapper.offsetHeight - window.innerHeight;
    const y = window.scrollY;
    return y >= top - 5 && y <= top + maxScroll + 5;
  }

  lockWheel() {
    this.isWheeling = true;
    if (this.wheelTimeout) {
      window.clearTimeout(this.wheelTimeout);
    }
    this.wheelTimeout = window.setTimeout(() => {
      this.isWheeling = false;
    }, 700);
  }

  scrollToStep(step, smooth = true) {
    if (!this.wrapper) return;
    const target = this.wrapper.offsetTop + step * window.innerHeight;
    window.scrollTo({ top: target, behavior: smooth ? "smooth" : "auto" });
  }

  clampStep(step) {
    return Math.max(0, Math.min(this.totalSteps, step));
  }

  applyState(step) {
    const safeStep = this.clampStep(step);
    const itemIndex = Math.min(this.items.length - 1, Math.floor(safeStep / 2));
    const panelVisible = safeStep % 2 === 1 || safeStep === this.totalSteps;

    this.items.forEach((item, idx) => {
      const isActive = idx === itemIndex;
      const offset = idx - itemIndex;
      item.style.setProperty("--ows-item-offset", String(offset));
      item.classList.toggle("is-active", isActive);
      item.classList.toggle("is-panel-visible", isActive && panelVisible);
    });
  }

  handleScroll() {
    if (!this.wrapper) return;
    const scrolled = window.scrollY - this.wrapper.offsetTop;
    const vh = window.innerHeight;
    const maxScroll = this.wrapper.offsetHeight - vh;
    const clamped = Math.max(0, Math.min(maxScroll, scrolled));
    let step = Math.floor((clamped + vh / 2) / vh);
    step = this.clampStep(step);

    if (step !== this.activeStep) {
      this.activeStep = step;
      this.applyState(this.activeStep);
    }
  }

  handleWheel(e) {
    if (!this.wrapper) return;
    if (!this.isStuck()) return;

    if (this.activeStep === 0 && e.deltaY < 0) return;
    if (this.activeStep === this.totalSteps && e.deltaY > 0) return;

    e.preventDefault();
    if (this.isWheeling) return;

    const nextStep = this.activeStep + (e.deltaY > 0 ? 1 : -1);
    this.scrollToStep(this.clampStep(nextStep), true);
    this.lockWheel();
  }

  handleTouchStart(e) {
    this.touchStartY = e.touches?.[0]?.clientY || 0;
  }

  handleTouchMove(e) {
    if (!this.wrapper || !this.isStuck() || !this.touchStartY) return;

    const touchEndY = e.touches?.[0]?.clientY || 0;
    const diffY = this.touchStartY - touchEndY;

    if (Math.abs(diffY) <= 30) return;
    if (this.activeStep === 0 && diffY < 0) return;
    if (this.activeStep === this.totalSteps && diffY > 0) return;

    e.preventDefault();
    if (!this.isWheeling) {
      const nextStep = this.activeStep + (diffY > 0 ? 1 : -1);
      this.scrollToStep(this.clampStep(nextStep), true);
      this.lockWheel();
    }

    this.touchStartY = touchEndY;
  }
}

if (!customElements.get("overview-wheel-story")) {
  customElements.define("overview-wheel-story", OverviewWheelStory);
}
